import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { createStockIssue } from '#/api/imsMgmt/receipts/stockIssue/create';
import { fetchStockIssueDetail } from '#/api/imsMgmt/receipts/stockIssue/fetchDetail';
import { updateStockIssue } from '#/api/imsMgmt/receipts/stockIssue/update';
import { fetchStockIssueCategorySelectOptions } from '#/api/system/stockIssueCategory/fetchSelectOptions';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import StockIssueLinesEditor from '#/modules/imsMgmt/receipts/stockIssue/components/StockIssueLinesEditor';
import {
  getStockIssueApiErrorMessage,
  isStockIssueVersionConflict,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/apiError';
import { canEditStockIssue } from '#/modules/imsMgmt/receipts/stockIssue/helper/issueStatus';
import {
  buildStockIssueWritePayload,
  mapStockIssueDetailToFormValues,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/formSerialize';
import {
  STOCK_ISSUE_FIELD_LABELS,
  stockIssueFormSchema,
} from '#/modules/imsMgmt/receipts/stockIssue/schemas/stockIssueSchema';
import {
  lookupQueryKey,
  stockIssueDetailQueryKey,
  stockIssueListQueryKeyPrefix,
} from '#/utils/queryKeys';

const DEFAULT_VALUES = {
  issueDate: '',
  stockIssueCategoryId: '',
  issueReason: '',
  remark: '',
  lines: [],
};

const CreateEditStockIssuePage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: stockIssueDetailQueryKey(id),
    queryFn: () => fetchStockIssueDetail(id),
    enabled: Boolean(id),
  });

  const categoryOptionsQuery = useQuery({
    queryKey: lookupQueryKey('stockIssueCategorySelect'),
    queryFn: fetchStockIssueCategorySelectOptions,
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createStockIssue,
    updateFn: updateStockIssue,
    buildPayload: (values) => buildStockIssueWritePayload(values, {
      updatedAt: id ? updatedAtRef.current : null,
    }),
    listQueryKeyPrefix: stockIssueListQueryKeyPrefix,
    detailQueryKey: stockIssueDetailQueryKey,
    getEditPath: ROUTES.stockIssueEdit,
    getErrorMessage: (error) => getStockIssueApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isStockIssueVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(stockIssueFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapStockIssueDetailToFormValues(detail));
  }, [detailQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const detail = detailQuery.data?.data;
  const pageTitle = isEdit ? '編輯出庫單' : '新增出庫單';
  const headerStatus = isEdit ? (detail?.status || 'DRAFT') : 'DRAFT';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const isReadOnlyEdit = isEdit && detail && !canEditStockIssue(detail.status);
  const categoryOptions = categoryOptionsQuery.data ?? [];

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              <DocumentStatusBadge status={headerStatus} size="sm" />
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.stockIssue}>
                返回列表
              </Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getStockIssueApiErrorMessage(detailQuery.error, '找不到出庫單')}
            </p>
          ) : null}

          {isReadOnlyEdit ? (
            <div className="space-y-3">
              <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>僅草稿可編輯。目前狀態為</span>
                <DocumentStatusBadge status={detail.status} size="sm" />
                <span>，請至列表或詳情頁操作。</span>
              </p>
              <Button type="button" asChild>
                <Link to={ROUTES.stockIssue}>返回列表</Link>
              </Button>
            </div>
          ) : null}

          {!isPageLoading && !isDetailNotFound && !isReadOnlyEdit ? (
            <div className="space-y-4">
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={STOCK_ISSUE_FIELD_LABELS}
                customMessages={customMessages}
              />

              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">單據資訊</h2>
                    </div>
                    <div
                      className={cn(
                        'grid grid-cols-1 gap-3',
                        '@4xl/sheet:grid-cols-2',
                      )}
                    >
                      <FloatingDateInput
                        id="issueDate"
                        label="出庫日"
                        control={control}
                        errors={errors}
                        placeholder="西元年/月/日"
                      />
                      <FloatingSelect
                        id="stockIssueCategoryId"
                        label="出庫類別"
                        options={categoryOptions}
                        control={control}
                        errors={errors}
                        isDisabled={categoryOptionsQuery.isLoading}
                        noOptionsMessage="無可用出庫類別"
                      />
                      <FloatingTextarea
                        id="issueReason"
                        label="出庫事由"
                        register={register}
                        errors={errors}
                      />
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                        rows={2}
                      />
                    </div>
                  </section>

                  <StockIssueLinesEditor
                    control={control}
                    register={register}
                    errors={errors}
                    getValues={getValues}
                    setValue={setValue}
                  />
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.stockIssue}>
                      取消
                    </Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditStockIssuePage;
